import { ApiError, extractErrorMessage, kindForStatus, toApiError } from './errors'

/**
 * Everything a request needs to know about *where* and *as whom* to call.
 * Passed in explicitly rather than read from a store, so the API layer stays
 * free of React and of global state.
 */
export interface ApiContext {
  /** Origin or path prefix, e.g. `http://127.0.0.1:8000` or `/api`. */
  baseUrl: string
  /** Host-issued JWT. Only sent when present — `/query/test` needs none. */
  bearerToken?: string
  /** Hard ceiling per request. LLM-backed queries are legitimately slow. */
  timeoutMs: number
}

export interface RequestOptions {
  path: string
  method?: 'GET' | 'POST'
  body?: unknown
  signal?: AbortSignal
  /** Overrides `ApiContext.timeoutMs` for this call. */
  timeoutMs?: number
}

export function joinUrl(baseUrl: string, path: string): string {
  const base = baseUrl.trim().replace(/\/+$/, '')
  const suffix = path.startsWith('/') ? path : `/${path}`
  return `${base}${suffix}`
}

/**
 * Combines the caller's abort signal with a timeout, and reports which one
 * fired — the UI says different things for "you cancelled" and "it hung".
 */
function withTimeout(
  timeoutMs: number,
  external?: AbortSignal,
): { signal: AbortSignal; cleanup: () => void; timedOut: () => boolean } {
  const controller = new AbortController()
  let timedOut = false

  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, timeoutMs)

  const forward = () => controller.abort()
  if (external) {
    if (external.aborted) forward()
    else external.addEventListener('abort', forward, { once: true })
  }

  return {
    signal: controller.signal,
    timedOut: () => timedOut,
    cleanup: () => {
      clearTimeout(timer)
      external?.removeEventListener('abort', forward)
    },
  }
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

/**
 * The one place an HTTP call is made. Returns parsed JSON on 2xx and throws an
 * `ApiError` on every other outcome.
 */
export async function request<T>(
  context: ApiContext,
  options: RequestOptions,
): Promise<T> {
  const { path, method = 'GET', body, signal } = options
  const timeoutMs = options.timeoutMs ?? context.timeoutMs

  if (!context.baseUrl.trim()) {
    throw new ApiError({
      kind: 'client',
      message: 'No API base URL is configured. Set one in Settings.',
    })
  }

  const timeout = withTimeout(timeoutMs, signal)

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (context.bearerToken) {
    headers.Authorization = `Bearer ${context.bearerToken}`
  }

  let response: Response
  try {
    response = await fetch(joinUrl(context.baseUrl, path), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: timeout.signal,
    })
  } catch (error) {
    if (timeout.timedOut()) {
      throw new ApiError({
        kind: 'timeout',
        message: `The request took longer than ${Math.round(timeoutMs / 1000)}s. Complex queries can exceed this — raise the timeout in Settings if it keeps happening.`,
        cause: error,
      })
    }
    throw toApiError(error, 'The request failed.')
  } finally {
    timeout.cleanup()
  }

  const payload = await readBody(response)

  if (!response.ok) {
    const { message, code } = extractErrorMessage(
      payload,
      `Request failed with status ${response.status}.`,
    )

    throw new ApiError({
      kind: kindForStatus(response.status),
      status: response.status,
      message,
      ...(code ? { code } : {}),
      detail: payload,
    })
  }

  if (payload === null) {
    throw new ApiError({
      kind: 'malformed',
      status: response.status,
      message: 'The server returned an empty response.',
    })
  }

  return payload as T
}
